import { gql, useLazyQuery } from "@apollo/client";
import DOMPurify from "dompurify";
import { functions, isEqual, omit } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useHistory } from "react-router-dom";
import { alert_circle, mail, message_square } from "src/images/NewDesign";
import { displaySanitizedHTML } from "src/utils/misc";
import { theme } from "src/utils/theme";
import { errorToast } from "src/utils/toast";
import styled from "styled-components";
import { AppText, FlexDiv, Loading } from "../UI";
import { PhoenixIcon, PhoenixMultiSelect } from "../UI/Phoenix";

const FETCH_INBOX = gql`
  query fetchInboxItems($skip: Int!, $take: Int!, $filters: InboxItemFilterInput) {
    fetchInboxItems(skip: $skip, take: $take, filters: $filters) {
      id
      lead {
        id
        business_name
      }
      email_item {
        id
        html
        body
      }
      sms_item {
        id
        text
      }
    }
  }
`;

const TAKE = 20;

interface IInboxListProps {
  debouncedSearch: string;
}

const typeFilterOptions = [
  { label: "All", value: [] },
  { label: "SMS", value: ["SMS"] },
  { label: "Email", value: ["EMAIL"] },
];

interface IInboxItem {
  id: string;
  lead: {
    id: string;
    business_name: string;
  };
  email_item?: {
    id: string;
    html?: string;
    body?: string;
  };
  sms_item?: {
    id: string;
    text: string;
  };
}

export const InboxList: React.FC<IInboxListProps> = ({ debouncedSearch }) => {
  const [inboxList, setInboxList] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [skip, setSkip] = useState<number>(0);

  const [type, setType] = useState<{ label: string; value: string[] } | undefined>(undefined);

  const [fetchInbox, { data: fetchInboxData, loading: fetchInboxLoading }] = useLazyQuery(FETCH_INBOX, {
    fetchPolicy: "cache-and-network",
    onCompleted: () => {
      const newList = [...inboxList, ...fetchInboxData.fetchInboxItems];

      if (fetchInboxData?.fetchInboxItems?.length < TAKE) setHasMore(false);
      else setHasMore(true);

      setInboxList(newList);
    },
    onError: () => {
      errorToast("Error fetching inbox");
    },
  });

  useEffect(() => {
    setInboxList([]);
    setSkip(0);
    setHasMore(true);

    fetchInbox({
      variables: {
        take: TAKE,
        skip: 0,
        filters: {
          type: type?.value || [],
          search: debouncedSearch,
        },
      },
    });
  }, [debouncedSearch, type]);

  const handleShowMore = useCallback(() => {
    fetchInbox({
      variables: {
        take: TAKE,
        skip: skip + TAKE,
        filters: {
          type: type?.value || [],
          search: debouncedSearch,
        },
      },
    });
    setSkip((prevSkip) => prevSkip + TAKE);
  }, [TAKE, skip, setSkip, debouncedSearch, type]);

  return (
    <>
      <FlexDiv justify="space-between" align="center" padding="0px 0px 8px 16px">
        <PhoenixMultiSelect
          isMulti={false}
          name="type"
          options={typeFilterOptions}
          placeholder="Type"
          onChange={(e: { label: string; value: string[] }) => {
            setType(e);
          }}
          value={type}
          isClearable={false}
          marginBottom={false}
          error={false}
          ghostSelect
          ghostSelectAppThemeOverride="Light"
          width={64}
          height={24}
          minHeight={24}
          optionsContainerWidth={120}
          style={{ overflow: "hidden" }}
          lineHeight="10px"
          fontSize="12px"
          pointer
          menuBorder
          removeMenuPadding
          menuPortal
        />
      </FlexDiv>

      <ListContainer id="inbox-scroll-div">
        <InfiniteScroll
          dataLength={inboxList.length}
          next={handleShowMore}
          hasMore={hasMore}
          loader={
            <div style={{ padding: "8px 0px" }}>
              <Loading />
            </div>
          }
          scrollableTarget="inbox-scroll-div"
        >
          {inboxList.map((item: any) => (
            <InboxItem key={item?.id} inboxItem={item} />
          ))}
        </InfiniteScroll>

        {!inboxList.length && !fetchInboxLoading && (
          <FlexDiv
            width="100%"
            height="100%"
            justify="center"
            align="center"
            direction="column"
            gap={24}
            padding="0px 0px 86px 0px"
          >
            <PhoenixIcon
              svg={message_square}
              size={40}
              color={theme.icon.neutral.default}
              hoverColor={theme.icon.neutral.default}
            />
            <AppText
              fontSize={14}
              fontWeight={400}
              lineHeight={20}
              color={theme.text.neutral.tertiary}
              textAlign="center"
              style={{ maxWidth: "270px" }}
            >
              {!!debouncedSearch.length
                ? "No results match your search criteria. Reset your filters or start a new search."
                : "No messages"}
            </AppText>
          </FlexDiv>
        )}
      </ListContainer>
    </>
  );
};

const ListContainer = styled.div`
  max-height: calc(100vh - 152px);
  height: 100%;
  overflow-y: auto;
`;

interface IInboxItemProps {
  inboxItem: IInboxItem;
}

const InboxItem: React.FC<IInboxItemProps> = React.memo(
  ({ inboxItem }) => {
    const history = useHistory();

    return (
      <InboxItemContainer>
        <FlexDiv align="center" justify="space-between" width="100%">
          <LeadIdentifierText
            fontSize={14}
            fontWeight={400}
            lineHeight={20}
            onClick={() => history.push(`/lead-detail/${inboxItem?.lead?.id}`)}
          >
            {inboxItem?.lead?.business_name || "N/A"}
          </LeadIdentifierText>

          <PhoenixIcon
            svg={!!inboxItem?.email_item ? mail : !!inboxItem?.sms_item ? message_square : alert_circle}
            size={16}
            color={theme.icon.brand.default}
            hoverColor={theme.icon.brand.default}
          />
        </FlexDiv>

        <AppText
          fontSize={14}
          fontWeight={400}
          lineHeight={20}
          color={theme.text.neutral.secondary}
          noWrap
          style={{ maxWidth: "390px", textOverflow: "ellipsis", overflow: "hidden" }}
        >
          {!!inboxItem?.email_item
            ? DOMPurify.sanitize(
                displaySanitizedHTML(inboxItem?.email_item?.html ?? (inboxItem?.email_item?.body || "")) || "",
              )
            : !!inboxItem?.sms_item?.text
            ? inboxItem?.sms_item?.text
            : "No content available..."}
        </AppText>
      </InboxItemContainer>
    );
  },
  (oldProps, newProps) => {
    const oldP = omit(oldProps, functions(oldProps));
    const newP = omit(newProps, functions(newProps));
    return isEqual(oldP, newP);
  },
);

const InboxItemContainer = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 8px;

  width: 100%;
  height: 80px;
  padding: 16px 24px;

  background-color: ${theme.fill.neutral.primary};
  border-bottom: 1px solid ${theme.border.neutral.secondary};
`;

const LeadIdentifierText = styled(AppText)`
  border-bottom: none;

  :hover {
    cursor: pointer;
    color: ${theme.text.brand.primary};
  }
  transition: color 0.2s ease-in-out;

  max-width: 375px;
  overflow: hidden;
  text-overflow: ellipsis;
`;
